import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { NavLink } from "react-router-dom";
import {
  Alert,
  AlertTitle,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent,
  Chip,
  CircularProgress,
  Divider as MuiDivider,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import styled from "@emotion/styled";
import { Box, spacing } from "@mui/system";
import { format } from "date-fns";
import useAuth from "../../../hooks/useAuth";
import useAppDispatch from "../../../hooks/useAppDispatch";
import useAppSelector from "../../../hooks/useAppSelector";
import { fetchDiagnostics } from "../../../redux/slices/accounting";

const Card = styled(MuiCard)(spacing);
const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

/**
 * Kirjanpidon tarkistus.
 *
 * Read-only on purpose. It reports what is wrong and what that means for the
 * books, and offers no fix buttons yet: showing people the damage does not
 * require the machinery to repair it, and correcting entries has to be a
 * deliberate, recorded act rather than a button that quietly rewrites history.
 */

const eur = (value: number) =>
  new Intl.NumberFormat("fi-FI", {
    style: "currency",
    currency: "EUR",
  }).format(value ?? 0);

const fi = (value: string | Date) => format(new Date(value), "dd.MM.yyyy");

const unitLabel = (count: number, unit: string) => {
  switch (unit) {
    case "transaction":
      return `${count} ${count === 1 ? "kirjaus" : "kirjausta"}`;
    case "account":
      return `${count} ${count === 1 ? "tili" : "tiliä"}`;
    case "fiscalYear":
      return `${count} ${count === 1 ? "tilikausi" : "tilikautta"}`;
    // A gap in the balance sheet is one fact about the whole book, not a count
    // of anything; the amount says everything the label could.
    case "report":
      return "koko kirjanpito";
    default:
      return `${count} ${count === 1 ? "tosite" : "tositetta"}`;
  }
};

/** The accounts table means something different per finding. */
const accountsHeading = (kind: string) =>
  kind === "REVERSED_ENTRY"
    ? "Tilit, joiden saldo on miinuksella"
    : "Tilit, joiden saldo jää nollaksi";

function Diagnostics() {
  const dispatch = useAppDispatch();
  const { currentUser } = useAuth();
  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const { diagnostics, diagnosticsStatus } = useAppSelector(
    (state: any) => state.accounting
  );

  useEffect(() => {
    if (associationId) dispatch(fetchDiagnostics(associationId));
  }, [dispatch, associationId]);

  const issues = diagnostics?.issues ?? [];

  return (
    <>
      <Helmet title="Kirjanpidon tarkistus" />
      <Typography variant="h3" gutterBottom display="inline">
        Kirjanpidon tarkistus
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          Etusivu
        </Link>
        <Link component={NavLink} to="/accounting">
          Talous
        </Link>
        <Typography>Kirjanpidon tarkistus</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      {diagnosticsStatus === "loading" && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {diagnosticsStatus === "failed" && (
        <Alert severity="error">
          Tarkistusta ei voitu suorittaa. Yritä hetken kuluttua uudelleen.
        </Alert>
      )}

      {diagnosticsStatus === "idle" && diagnostics?.clean && (
        <Alert severity="success">
          <AlertTitle>Kirjanpidossa ei havaittu ongelmia</AlertTitle>
          Tarkistimme tositteet, tilit ja tilikaudet emmekä löytäneet
          korjattavaa.
        </Alert>
      )}

      {diagnosticsStatus === "idle" && !diagnostics?.clean && (
        <>
          <Alert severity="info" sx={{ mb: 4 }}>
            Tämä sivu vain kertoo, mitä kirjanpidossa on vialla. Se ei muuta
            eikä korjaa mitään.
          </Alert>

          {issues.map((issue: any) => (
            <Card mb={6} key={issue.kind}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "wrap",
                    mb: 1,
                  }}
                >
                  <Typography variant="h6">{issue.title}</Typography>
                  <Chip
                    size="small"
                    label={unitLabel(issue.count, issue.unit)}
                    color={issue.severity === "critical" ? "error" : "warning"}
                  />
                </Box>

                <Typography variant="body2" color="textSecondary">
                  {issue.consequence}
                </Typography>

                {/* The both-sides case: the accounts whose whole movement
                    cancels out. This is the number a treasurer is missing from
                    their income statement without knowing it. */}
                {issue.accounts?.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {accountsHeading(issue.kind)}
                    </Typography>
                    <Paper variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Tili</TableCell>
                            <TableCell>Nimi</TableCell>
                            <TableCell align="right">Saldo</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {issue.accounts.map((account: any) => (
                            <TableRow key={account.code}>
                              <TableCell>{account.code}</TableCell>
                              <TableCell>{account.name ?? "—"}</TableCell>
                              <TableCell align="right">
                                {eur(account.net)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Paper>
                  </Box>
                )}

                {issue.fiscalYears?.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Tilikaudet
                    </Typography>
                    <Paper variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Alkaa</TableCell>
                            <TableCell>Päättyy</TableCell>
                            <TableCell align="right">Pituus</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {issue.fiscalYears.map((year: any) => (
                            <TableRow key={year.fiscalYearId}>
                              <TableCell>{fi(year.from)}</TableCell>
                              <TableCell>{fi(year.to)}</TableCell>
                              <TableCell align="right">
                                {year.months} kk
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Paper>
                  </Box>
                )}

                {issue.examples?.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Esimerkkejä
                      {issue.count > issue.examples.length &&
                        ` (${issue.examples.length} / ${issue.count})`}
                    </Typography>
                    <Paper variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Pvm</TableCell>
                            <TableCell>Kuvaus</TableCell>
                            <TableCell>Huomio</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {issue.examples.map((example: any) => (
                            <TableRow key={example.voucherId}>
                              <TableCell>
                                {example.date
                                  ? format(new Date(example.date), "dd.MM.yyyy")
                                  : "—"}
                              </TableCell>
                              <TableCell>{example.description}</TableCell>
                              <TableCell>{example.detail ?? ""}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Paper>
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}

          {diagnostics?.checkedAt && (
            <Typography variant="caption" color="textSecondary">
              Tarkistettu{" "}
              {format(new Date(diagnostics.checkedAt), "dd.MM.yyyy HH:mm")}
            </Typography>
          )}
        </>
      )}
    </>
  );
}

export default Diagnostics;
